import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useAwaitControl } from 'react-redux-await-control';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Avatar, Tooltip as TooltipAntd } from 'antd';

import getInitials from '~/common/getInitials';
import masked from '~/common/masked';
import { Shimmer, Tooltip } from '~/ui/components';

import { Person, PersonRegistrationLog } from '~/typings/entities/person';
import toPhone from '~/common/masked/toPhone';
import { getPersonRegistrationLogs } from '~/store/people/actions';
import { formatName } from '~/common';
import { ClixClient } from '~/typings/entities/Clix';
import { Clock, Check, Close, UserSquare, CaretDown, Tag } from '~/ui/assets/icons';
import toCPFFormat from '~/common/masked/toCPFFormat';
import toDate from '~/common/masked/toDate';
import { TagPopover } from '~/ui/components/Tags/TagPopover';
import { addTag, listTags } from '~/store/tags/actions';
import { listTagTypes } from '~/store/tagTypes/actions';
import saveTagTypes from '~/store/tagTypesSave/action';
import useUserRoles from '~/hooks/useUserRoles';
import { useDispatch } from 'react-redux';
import { ProfileTags } from '../ProfileTags/ProfileTags';
import { RefusedPopover } from './RefusedPopover';
import {
  ProfileResumeContainer,
  ProfileInitials,
  ProfileImageContainer,
  ProfileName,
  ProfileInfo,
  ProfileData,
  ProfileInfoWithPopover,
  CopyButton,
  Copied,
  NameContent,
  ProfileWrapper,
  Icon,
  Bagde,
  ProfileContainer,
  ProfileInitialInfoContainer,
  ProfileInitialInfoFooter,
  ProfileDataLastAcess,
  HistoryWrapper,
  ProfileInfoWrapper,
  ProfileInfoSkeleton,
  ProfileContainerSkeleton,
  ProfileInfoSkeletonActions,
  LoadingContainer,
  TagsWrapper,
  IconsWrapper,
  TooltipBody,
  TagContainer,
  ClockTooltipWrapper,
} from './ProfileResume.styled';

interface ProfileResumeProps {
  person: Person;
  clixDetails?: ClixClient;
  loading?: boolean;
  image?: any;
  responsiveActions?: React.ReactNode;
  className?: string;
}

export function ProfileResume({ person, loading, image, responsiveActions, ...props }: ProfileResumeProps) {
  const [copied, setCopied] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { isAdmin, isSupervisor } = useUserRoles();

  const listPersonRegistrationLogControl = useAwaitControl(getPersonRegistrationLogs);
  const listTagsControl = useAwaitControl(listTags);
  const listTagTypesControl = useAwaitControl(listTagTypes);
  const addTagControl = useAwaitControl(addTag);

  const isPersonPage = history.location.pathname.startsWith('/backoffice/people');

  const canceledByClient = useMemo(() => person?.nameUserWhoCancel === person?.name, [person]);

  const tagsType = 'PERSON';
  const tagsExternalId = person?.id;
  const actionId = `${tagsType}_${tagsExternalId}`;

  const tagsProfile = listTagsControl.result(actionId);
  const tagOptions = listTagTypesControl.result(tagsType);

  const formatDate = useCallback((dateToFormat, isDateAndHour = true) => {
    if (!dateToFormat) return 'Indisponível';

    if (isDateAndHour) {
      return moment(dateToFormat).format('DD/MM/YYYY [às] HH:mm');
    }

    return moment(dateToFormat).format('DD/MM/YYYY');
  }, []);

  const humanizeDate = useCallback((dateToFormat) => {
    if (!dateToFormat) return 'Indisponível';

    return masked.fromNow(dateToFormat);
  }, []);

  const imageOrInitials = useMemo(
    () =>
      image ? (
        <Avatar className="avatar" shape="circle" src={image} />
      ) : (
        <ProfileInitials>{getInitials(person?.name)}</ProfileInitials>
      ),
    [image, person?.name],
  );

  const registerTag = useMemo(() => {
    if (['REFUSED'].includes(person?.registrationStatus)) {
      return {
        label: 'Reprovado',
        badge: <Close width={13} height={13} data-testid="rejected-registration" />,
      };
    }

    if (['APPROVED', 'COMPLETE'].includes(person?.registrationStatus)) {
      return {
        label: 'Aprovado',
        badge: <Check width={9} height={9} data-testid="approved-registration" />,
      };
    }

    return null;
  }, [person]);

  const handleCopy = useCallback(() => {
    const clipBoard = navigator.clipboard;

    clipBoard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  }, [window.location.href]);

  const clockTooltipContent = (
    <ClockTooltipWrapper>
      <span>Último acesso app/web:</span>
      <span className="date-formated">{formatDate(person?.lastAcess)}</span>
    </ClockTooltipWrapper>
  );

  const tags = useMemo(
    () =>
      Array.isArray(tagsProfile)
        ? tagsProfile?.map(({ tagType, id }) => ({
            ...tagType,
            tagId: id,
            bgColor: 'brand.secondary',
            textColor: 'brand.primaryAlt',
          }))
        : [],
    [tagsProfile],
  );

  const copyButton = useMemo(
    () => (
      <Tooltip className="copyButton" content={copied ? 'COPIADO!' : 'COPIAR LINK DO CLIENTE'} small>
        {copied ? (
          <Copied data-testid="copied-check-icon" />
        ) : (
          <CopyButton data-testid="copy-button" onClick={handleCopy} />
        )}
      </Tooltip>
    ),
    [copied],
  );

  const handleAddTag = useCallback(
    (tag) => {
      addTagControl.start({ externalId: tagsExternalId, tagTypeId: tag.id, type: tagsType });
    },
    [tagsExternalId, tagsType],
  );

  const handleCreateTag = (tagName: string) => {
    if (tagName && (isAdmin || isSupervisor)) {
      dispatch(saveTagTypes({ externalId: tagsExternalId, name: tagName, tagResourceType: tagsType }));
    }
  };

  useEffect(() => {
    if (person?.id) {
      listPersonRegistrationLogControl.start({ personId: person.id });
    }
  }, [person]);

  const personRegistrationLog = listPersonRegistrationLogControl.result() as PersonRegistrationLog[];

  return (
    <ProfileResumeContainer {...props}>
      {loading ? (
        <LoadingContainer>
          <ProfileContainerSkeleton>
            <Shimmer circular width={88} height={88} />
            <ProfileInfoSkeleton>
              <Shimmer width={220} height={20} />
              <Shimmer width={185} height={20} />
              <Shimmer width={160} height={20} />
              <Shimmer width={160} height={20} />
              <Shimmer width={100} height={20} />
            </ProfileInfoSkeleton>
          </ProfileContainerSkeleton>
          <ProfileInfoSkeletonActions>
            <Shimmer width={220} height={20} />
            <Shimmer width={145} height={20} />
            <Shimmer width={220} height={20} />
          </ProfileInfoSkeletonActions>
        </LoadingContainer>
      ) : (
        <>
          <ProfileContainer>
            <ProfileWrapper>
              <ProfileImageContainer>
                {imageOrInitials}
                {registerTag && (
                  <Tooltip content={registerTag.label} small placement="top">
                    <Bagde status={registerTag.label}>{registerTag.badge}</Bagde>
                  </Tooltip>
                )}
              </ProfileImageContainer>
              <ProfileInfo>
                <NameContent>
                  {!isPersonPage ? (
                    <>
                      <Link to={`/backoffice/people/${person?.id}`}>
                        <ProfileName withLink>{formatName(person?.name)}</ProfileName>
                      </Link>
                      <Icon>{copyButton}</Icon>
                    </>
                  ) : (
                    <>
                      <ProfileName>{formatName(person?.name)}</ProfileName>
                      <Icon>{copyButton}</Icon>
                    </>
                  )}
                </NameContent>

                <ProfileInfoWrapper flexWrap className="profileInfoWrapper">
                  {person?.id && (
                    <ProfileInfoWithPopover>
                      <ProfileData>Person ID:</ProfileData>
                      <ProfileData copyable>{person?.id}</ProfileData>
                    </ProfileInfoWithPopover>
                  )}
                  <ProfileInitialInfoContainer>
                    {person?.birthDate ? (
                      <TooltipAntd title={toDate(person?.birthDate)} placement="top">
                        <ProfileData copyable>{humanizeDate(person?.birthDate)} de idade</ProfileData>
                      </TooltipAntd>
                    ) : (
                      <ProfileData>Idade indisponível</ProfileData>
                    )}
                    {person?.phone && (
                      <ProfileData copyable copyText={person.phone}>
                        {toPhone(person.phone)}
                      </ProfileData>
                    )}
                    {person?.document && (
                      <ProfileData copyable copyText={person.document}>
                        {toCPFFormat(person.document)}
                      </ProfileData>
                    )}
                  </ProfileInitialInfoContainer>
                  {person?.email && <ProfileData copyable>{person.email}</ProfileData>}
                  {/* {!!person?.disabled && ( COMENTADO
                    <ProfileInfoWithPopover>
                      {canceledByClient ? (
                        'Cancelado pelo cliente'
                      ) : (
                        <>
                          Agente que reprovou: <b>{person?.nameUserWhoCancel}</b>
                        </>
                      )}
                      <CancelPopover canceledByClient={canceledByClient} person={person} />
                    </ProfileInfoWithPopover>
                  )} */}
                  <ProfileInitialInfoFooter>
                    <ProfileDataLastAcess>
                      <Clock width={18} height={18} />
                      Último acesso app/web: {formatDate(person?.lastAcess)}
                    </ProfileDataLastAcess>

                    {personRegistrationLog?.length > 0 && (
                      <HistoryWrapper>
                        <ProfileInfoWithPopover>
                          <UserSquare width={20} height={20} />
                          <span className="text">Histórico de cadastro</span>
                          <RefusedPopover
                            registrationLog={personRegistrationLog}
                            icon={<CaretDown className="action-icon" width={14} height={14} />}
                          />
                        </ProfileInfoWithPopover>
                      </HistoryWrapper>
                    )}

                    <TagsWrapper>
                      <ProfileTags externalId={tagsExternalId} type={tagsType} />
                    </TagsWrapper>
                  </ProfileInitialInfoFooter>
                </ProfileInfoWrapper>
                <IconsWrapper>
                  <TooltipAntd title={clockTooltipContent} placement="top">
                    <TooltipBody>
                      <Clock width={18} height={18} />
                    </TooltipBody>
                  </TooltipAntd>
                  <Tooltip title="Histórico de cadastro" placement="top">
                    <TooltipBody>
                      <RefusedPopover
                        registrationLog={personRegistrationLog}
                        icon={<UserSquare width={20} height={20} />}
                      />
                    </TooltipBody>
                  </Tooltip>
                  <Tooltip title="Tags" placement="top">
                    <TooltipBody>
                      <TagPopover
                        tags={tags}
                        editable
                        options={tagOptions}
                        onAdd={handleAddTag}
                        onCreate={handleCreateTag}
                        closable={isAdmin || isSupervisor}
                      >
                        <TagContainer>
                          <Tag width={20} height={20} />
                        </TagContainer>
                      </TagPopover>
                    </TooltipBody>
                  </Tooltip>
                </IconsWrapper>
              </ProfileInfo>
            </ProfileWrapper>
            {responsiveActions}
          </ProfileContainer>
        </>
      )}
    </ProfileResumeContainer>
  );
}
